import { type FormEvent, type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';

const { t } = i18n;

export function CustomerNewPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [billingMode, setBillingMode] = useState<'PER_PALLET_DAY' | 'FIXED_MONTHLY'>('PER_PALLET_DAY');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const created = await api<{ id: string }>('/customers', {
        method: 'POST',
        body: {
          code,
          name,
          billingMode,
          contactEmail: contactEmail || undefined,
          contactPhone: contactPhone || undefined,
        },
      });
      navigate(`/customers/${created.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'customer_code_taken') {
        setError(t('customer.create.code_taken'));
      } else if (err instanceof ApiError && err.code === 'invalid_input') {
        setError(t('customer.create.invalid_input'));
      } else {
        setError(t('common.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="max-w-md space-y-4">
      <h2 className="text-xl font-semibold">{t('customer.create.title')}</h2>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
        <Field label={t('customer.col.code')} hint="e.g. ARCH">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
            pattern="[A-Z0-9]{2,16}"
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm font-mono"
          />
        </Field>
        <Field label={t('customer.col.name')}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label={t('customer.col.billing_mode')}>
          <select
            value={billingMode}
            onChange={(e) => setBillingMode(e.target.value as typeof billingMode)}
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
          >
            <option value="PER_PALLET_DAY">{t('customer.billing.PER_PALLET_DAY')}</option>
            <option value="FIXED_MONTHLY">{t('customer.billing.FIXED_MONTHLY')}</option>
          </select>
        </Field>
        <Field label={t('customer.contact.email')}>
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label={t('customer.contact.phone')}>
          <input
            type="tel"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
          />
        </Field>

        {error ? <div className="text-sm text-red-600">{error}</div> : null}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={busy || !code || !name}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('common.save')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/customers')}
            className="rounded border border-border px-3 py-1.5 text-sm"
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {hint ? <span className="block text-xs text-muted-foreground">{hint}</span> : null}
    </label>
  );
}
